import React, { useRef, useState } from "react";
import Dialog from "@mui/material/Dialog";
import DialogContent from "@mui/material/DialogContent";
import Swal from "sweetalert2";
import { addGHPayment, GH_PAYMENT_METHODS } from "../../../services/guestHouseServicesApi";

export default function AddPaymentModal({ open, onClose, record, onSuccess }) {
  const [loading, setLoading] = useState(false);
  const loadingRef = useRef(false);
  const [formData, setFormData] = useState({
    paid_amount: "",
    paymentMethod: "UPI",
    payment_Date: new Date().toISOString().slice(0, 10),
    paid_to: "",
    paid_for: "",
    notes: "",
  });
  const [files, setFiles] = useState([]);

  if (!open || !record) return null;

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData((prev) => ({ ...prev, [name]: value }));
  };

  const handleFileChange = (e) => {
    if (e.target.files) {
      setFiles(Array.from(e.target.files));
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (loadingRef.current) return;
    const amount = Number(formData.paid_amount);
    if (!amount || amount <= 0) {
      Swal.fire("Validation Error", "Please enter a valid payment amount.", "warning");
      return;
    }

    loadingRef.current = true;
    setLoading(true);
    try {
      const payload = new FormData();
      payload.append("paid_amount", String(amount));
      payload.append("paymentMethod", formData.paymentMethod);
      payload.append("payment_Date", formData.payment_Date);
      if (formData.notes) payload.append("notes", formData.notes);
      if (formData.paid_to) payload.append("paid_to", formData.paid_to);
      if (formData.paid_for) payload.append("paid_for", formData.paid_for);

      files.forEach((file) => {
        payload.append("invoiceFiles", file);
      });

      const res = await addGHPayment(record._id || record.guestHouseServiceId, payload);
      Swal.fire({
        title: "Payment Recorded!",
        text: res?.message || `Payment of ₹${amount.toLocaleString()} received successfully.`,
        icon: "success",
        timer: 1800,
        showConfirmButton: false,
      });

      if (onSuccess) onSuccess();
      onClose();
    } catch (err) {
      console.error("Error recording payment:", err);
      Swal.fire("Error", typeof err === "string" ? err : "Failed to record payment", "error");
    } finally {
      loadingRef.current = false;
      setLoading(false);
    }
  };

  const dueAmount = Number(record.dueAmount || (Number(record.final_amount || record.totalAmount || 0) - Number(record.paidAmount || 0))) || 0;

  return (
    <Dialog
      open={open}
      onClose={onClose}
      maxWidth="sm"
      fullWidth
      className="gh-modal-dialog"
      scroll="paper"
      disableAutoFocus
      disableRestoreFocus
      PaperProps={{
        style: {
          borderRadius: "16px",
          display: "flex",
          flexDirection: "column",
          overflow: "hidden",
          maxHeight: "calc(100vh - 48px)",
          boxShadow: "0 25px 50px -12px rgba(0, 0, 0, 0.35)",
        },
      }}
    >
      <div
        className="gh-modal-header"
        style={{
          background: "linear-gradient(135deg, #059669 0%, #047857 100%)",
          padding: "16px 20px",
          display: "flex",
          justifyContent: "space-between",
          alignItems: "center",
          color: "#fff",
          flexShrink: 0,
        }}
      >
        <div className="d-flex align-items-center gap-2">
          <div
            style={{
              width: "36px",
              height: "36px",
              borderRadius: "50%",
              background: "rgba(255,255,255,0.2)",
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
            }}
          >
            <i className="fa fa-credit-card fs-5" />
          </div>
          <div>
            <h5 className="mb-0 fw-bold" style={{ fontSize: "16px" }}>
              Record Payment Received
            </h5>
            <small style={{ color: "rgba(255,255,255,0.85)" }}>
              {record.guestHouseServiceId} • {record.patient_name}
            </small>
          </div>
        </div>
        <button
          type="button"
          onClick={onClose}
          aria-label="Close"
          style={{
            background: "rgba(255,255,255,0.15)",
            border: "none",
            color: "#fff",
            width: "32px",
            height: "32px",
            borderRadius: "50%",
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            fontSize: "16px",
            cursor: "pointer",
            transition: "background 0.2s ease",
          }}
          onMouseEnter={(e) => (e.currentTarget.style.background = "rgba(255,255,255,0.3)")}
          onMouseLeave={(e) => (e.currentTarget.style.background = "rgba(255,255,255,0.15)")}
        >
          <i className="fa fa-times" />
        </button>
      </div>

      <DialogContent style={{ padding: "20px", flex: "1 1 auto", overflowY: "auto" }}>
        {/* Financial Highlights */}
        <div className="p-3 mb-3 rounded border bg-light d-flex justify-content-between align-items-center flex-wrap gap-2">
          <div>
            <span className="small text-muted d-block">Total Billing</span>
            <strong className="fs-6 text-dark">
              ₹{(Number(record.final_amount || record.totalAmount || 0)).toLocaleString()}
            </strong>
          </div>
          <div>
            <span className="small text-muted d-block">Already Paid</span>
            <span className="badge bg-success fs-6">
              ₹{(Number(record.paidAmount || 0)).toLocaleString()}
            </span>
          </div>
          <div>
            <span className="small text-muted d-block">Outstanding Balance</span>
            <span className={`badge fs-6 ${dueAmount > 0 ? "bg-danger" : "bg-secondary"}`}>
              ₹{dueAmount.toLocaleString()}
            </span>
          </div>
        </div>

        <form onSubmit={handleSubmit}>
          <div className="row g-3">
            <div className="col-md-6">
              <label className="form-label small fw-bold text-dark">
                Payment Amount (₹) <span className="text-danger">*</span>
              </label>
              <div className="input-group">
                <span className="input-group-text">₹</span>
                <input
                  type="number"
                  min="1"
                  step="any"
                  required
                  name="paid_amount"
                  placeholder={dueAmount > 0 ? `e.g. ${dueAmount}` : "0"}
                  value={formData.paid_amount}
                  onChange={handleChange}
                  className="form-control"
                  style={{ fontWeight: 600 }}
                />
              </div>
              {dueAmount > 0 && (
                <button
                  type="button"
                  className="btn btn-link btn-sm p-0 mt-1 text-primary text-decoration-none"
                  style={{ fontSize: "11.5px" }}
                  onClick={() => setFormData((prev) => ({ ...prev, paid_amount: String(dueAmount) }))}
                >
                  Fill full balance (₹{dueAmount.toLocaleString()})
                </button>
              )}
            </div>

            <div className="col-md-6">
              <label className="form-label small fw-bold text-dark">
                Payment Method <span className="text-danger">*</span>
              </label>
              <select
                name="paymentMethod"
                value={formData.paymentMethod}
                onChange={handleChange}
                className="form-select"
                required
              >
                {GH_PAYMENT_METHODS.map((m) => (
                  <option key={m} value={m}>
                    {m}
                  </option>
                ))}
              </select>
            </div>

            <div className="col-md-6">
              <label className="form-label small fw-bold text-dark">Payment Date</label>
              <input
                type="date"
                name="payment_Date"
                value={formData.payment_Date}
                onChange={handleChange}
                className="form-control"
              />
            </div>

            <div className="col-md-6">
              <label className="form-label small fw-bold text-dark">Upload Receipts / Invoices</label>
              <input
                type="file"
                multiple
                accept="image/*,.pdf"
                onChange={handleFileChange}
                className="form-control"
              />
              <small className="text-muted" style={{ fontSize: "11px" }}>
                Accepts images or PDF receipts
              </small>
            </div>

            <div className="col-12">
              <label className="form-label small fw-bold text-dark">Payment Remarks / Transaction ID</label>
              <textarea
                rows="2"
                name="notes"
                placeholder="e.g. Paid via Google Pay ref #498102, received by reception"
                value={formData.notes}
                onChange={handleChange}
                className="form-control"
              />
            </div>
          </div>

          <div className="d-flex justify-content-end gap-2 mt-4 pt-3 border-top">
            <button
              type="button"
              className="btn btn-light btn-sm px-3"
              onClick={onClose}
              disabled={loading}
            >
              Cancel
            </button>
            <button
              type="submit"
              className="btn btn-success btn-sm px-4 fw-bold d-flex align-items-center gap-2"
              disabled={loading}
              style={{ backgroundColor: "#059669", borderColor: "#059669" }}
            >
              {loading ? (
                <>
                  <i className="fa fa-spinner fa-spin" /> Recording...
                </>
              ) : (
                <>
                  <i className="fa fa-check" /> Record Payment
                </>
              )}
            </button>
          </div>
        </form>
      </DialogContent>
    </Dialog>
  );
}
